import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Box, Newspaper, FileText, Wrench, ClipboardCheck, Truck, Users, ArrowRight,
  Activity, AlertCircle, TrendingUp,
} from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import Counter from "../../components/Counter";

/* Top priority — visible to ALL users (admin + staff) */
const PRIORITY_CARDS = [
  {
    key: "service_reports",
    label: "Service Reports",
    icon: Wrench,
    to: "/admin/service-reports",
    accent: "bg-amber-500",
    desc: "Field service activities",
  },
  {
    key: "installation_reports",
    label: "Installation Reports",
    icon: ClipboardCheck,
    to: "/admin/installation-reports",
    accent: "bg-blue-500",
    desc: "Equipment installations",
  },
  {
    key: "vehicle_reports",
    label: "Vehicle Installations",
    icon: Truck,
    to: "/admin/vehicle-reports",
    accent: "bg-emerald-500",
    desc: "GPS / telematics installs",
  },
  {
    key: "rfq_total",
    label: "Quotation Requests",
    icon: FileText,
    to: "/admin/quotations",
    accent: "bg-rose-500",
    desc: "Customer enquiries",
  },
];

/* Admin-only — visible only to admin role */
const ADMIN_CARDS = [
  { key: "products", label: "Products", icon: Box, to: "/admin/products" },
  { key: "news", label: "News Articles", icon: Newspaper, to: "/admin/news" },
  { key: "users", label: "Active Users", icon: Users, to: "/admin/users" },
];

export default function AdminDashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [stats, setStats] = useState({});

  useEffect(() => {
    api.get("/settings/stats").then((r) => setStats(r.data));
  }, []);

  return (
    <div data-testid="admin-dashboard">
      {/* Header strip */}
      <div className="bg-slate-950 text-white p-6 md:p-8 mb-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-30" />
        <div className="absolute right-0 top-0 h-full w-32 bg-diagonal opacity-30 hidden md:block" />
        <div className="relative">
          <div className="text-amber-400 overline mb-2">
            {isAdmin ? "Admin Console" : "Staff Portal"} · Operations Overview
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tight">
            Welcome back, <span className="text-amber-400">{user?.name?.split(" ")[0]}</span>
          </h1>
          <p className="text-slate-300 text-sm mt-2 max-w-2xl">
            {isAdmin
              ? "Manage products, news, customer enquiries, and field reports — all in one place."
              : "Submit field reports and customer quotations. All submissions auto-generate branded PDFs."}
          </p>
        </div>
      </div>

      {/* New Enquiries Alert (shown only when there's something) */}
      {stats.rfq_new > 0 && (
        <Link
          to="/admin/quotations"
          className="block mb-8 bg-amber-50 border-l-4 border-amber-500 p-4 hover:bg-amber-100 transition-colors group"
          data-testid="new-enquiries-alert"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600" />
              <div>
                <div className="font-bold text-slate-900">
                  {stats.rfq_new} new {stats.rfq_new === 1 ? "enquiry" : "enquiries"} awaiting response
                </div>
                <div className="text-xs text-slate-600">Click to view and respond from the inbox.</div>
              </div>
            </div>
            <ArrowRight className="w-5 h-5 text-amber-600 group-hover:translate-x-1 transition-transform" />
          </div>
        </Link>
      )}

      {/* PRIORITY CARDS — Reports first (top section) */}
      <div className="mb-4">
        <div className="overline mb-3 flex items-center gap-2">
          <Activity className="w-4 h-4" />
          Field Reports & Quotations
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
        {PRIORITY_CARDS.map((c) => {
          const Icon = c.icon;
          const v = stats[c.key];
          return (
            <Link
              key={c.key}
              to={c.to}
              data-testid={`stat-${c.key}`}
              className="group bg-white border border-slate-200 hover:border-slate-900 transition-colors card-lift block"
            >
              <div className={`${c.accent} h-1.5 w-full`} />
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className={`${c.accent} w-12 h-12 grid place-items-center`}>
                    <Icon className="w-6 h-6 text-white" strokeWidth={2.2} />
                  </div>
                  <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-slate-900 group-hover:translate-x-1 transition-all" />
                </div>
                <div className="font-heading font-black text-4xl md:text-5xl text-slate-950 leading-none">
                  {v !== undefined ? <Counter to={v} /> : "—"}
                </div>
                <div className="text-sm font-bold text-slate-900 mt-3">{c.label}</div>
                <div className="text-xs text-slate-500 mt-1">{c.desc}</div>
              </div>
            </Link>
          );
        })}
      </div>

      {/* ADMIN-ONLY: Website Management */}
      {isAdmin && (
        <>
          <div className="mb-4">
            <div className="overline mb-3 flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Website Management
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-5 mb-12">
            {ADMIN_CARDS.map((c) => {
              const Icon = c.icon;
              const v = stats[c.key];
              return (
                <Link
                  key={c.key}
                  to={c.to}
                  data-testid={`stat-${c.key}`}
                  className="group bg-slate-50 border border-slate-200 hover:bg-white hover:border-amber-500 transition-colors card-lift block p-6"
                >
                  <div className="flex items-center justify-between">
                    <Icon className="w-6 h-6 text-slate-700" strokeWidth={2} />
                    <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-amber-600 group-hover:translate-x-1 transition-all" />
                  </div>
                  <div className="mt-4 font-heading font-black text-3xl text-slate-900">
                    {v !== undefined ? <Counter to={v} /> : "—"}
                  </div>
                  <div className="text-xs uppercase tracking-widest font-semibold text-slate-500 mt-1">
                    {c.label}
                  </div>
                </Link>
              );
            })}
          </div>
        </>
      )}

      {/* Quick Actions + Tips */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 p-6">
          <div className="overline mb-2">Quick Actions</div>
          <h3 className="font-heading text-lg font-bold mb-4">Submit a new report</h3>
          <div className="space-y-2">
            <Link to="/admin/service-reports" className="flex items-center justify-between p-3 hover:bg-amber-50 hover:border-amber-500 border border-slate-200 group">
              <span className="text-sm font-semibold flex items-center gap-2">
                <Wrench className="w-4 h-4 text-amber-500" /> + New Service Report
              </span>
              <ArrowRight className="w-4 h-4 text-amber-500 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/admin/installation-reports" className="flex items-center justify-between p-3 hover:bg-amber-50 hover:border-amber-500 border border-slate-200 group">
              <span className="text-sm font-semibold flex items-center gap-2">
                <ClipboardCheck className="w-4 h-4 text-amber-500" /> + New Installation Report
              </span>
              <ArrowRight className="w-4 h-4 text-amber-500 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/admin/vehicle-reports" className="flex items-center justify-between p-3 hover:bg-amber-50 hover:border-amber-500 border border-slate-200 group">
              <span className="text-sm font-semibold flex items-center gap-2">
                <Truck className="w-4 h-4 text-amber-500" /> + New Vehicle Installation
              </span>
              <ArrowRight className="w-4 h-4 text-amber-500 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
        <div className="bg-slate-950 text-white p-6 relative overflow-hidden">
          <div className="absolute inset-0 bg-grid opacity-30" />
          <div className="relative">
            <div className="overline text-amber-400 mb-2">Pro Tip</div>
            <h3 className="font-heading text-lg font-bold mb-2">Branded PDFs auto-generated</h3>
            <p className="text-sm text-slate-300 leading-relaxed">
              Every report submission auto-generates a branded company PDF with report number,
              engineer details, customer info, and signatures. Click the download icon next to
              any report to share it via WhatsApp or email.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
